import { useMentionCardsContext } from "@/containers/MentionCardsContext";
import { EpisodeV4, MentionV4 } from "@/types/MentionCard";
import {
  useMediaDispatch,
  useMediaSelector,
  MediaActionTypes,
} from "media-chrome/react/media-store";
import { useRef, useEffect } from "react";

export default function AutoSkip({
  mentions,
  episodeId,
}: {
  mentions: MentionV4[];
  episodeId: EpisodeV4["id"];
}) {
  const { activeEpisodeId } = useMentionCardsContext();
  const dispatch = useMediaDispatch();
  const mediaCurrentTime =
    useMediaSelector((state) => state.mediaCurrentTime) ?? 0;
  const mediaPaused = useMediaSelector((state) => state.mediaPaused);

  const isActiveEpisode = activeEpisodeId === episodeId;

  // Track which mention we were last inside
  const activeMentionIdRef = useRef<string | null>(null);

  // Reset the ref when this episode becomes active to prevent stale state
  // from causing immediate pause when switching between cards
  useEffect(() => {
    if (isActiveEpisode) {
      activeMentionIdRef.current = null;
    }
  }, [isActiveEpisode]);

  useEffect(() => {
    if (mediaPaused || !isActiveEpisode) return;

    // Find the mention we are currently inside
    const activeMention = mentions.find(
      (m) =>
        mediaCurrentTime >= (m.indices_metadata?.start_time ?? 0) &&
        mediaCurrentTime <= (m.indices_metadata?.end_time ?? 0)
    );

    const prevMentionId = activeMentionIdRef.current;

    // Update ref to current
    activeMentionIdRef.current = activeMention?.id ?? null;

    // If we were in a mention and now we're not, we just exited
    if (prevMentionId && !activeMention) {
      const prevIndex = mentions.findIndex((m) => m.id === prevMentionId);
      const nextMention = mentions[prevIndex + 1];

      if (nextMention) {
        dispatch({
          type: MediaActionTypes.MEDIA_SEEK_REQUEST,
          detail: nextMention.indices_metadata?.start_time,
        });
      } else {
        // No more mentions - pause
        dispatch({ type: MediaActionTypes.MEDIA_PAUSE_REQUEST });
      }
    }
  }, [mediaCurrentTime, mediaPaused, mentions, dispatch, isActiveEpisode]);

  return null;
}
